import { useState } from 'react';
import { toast } from 'sonner';
import { captureBoxScore, dataUrlToBlob, downloadDataUrl } from './boxScoreShare';

const landscapeName = (name) => name.replace('.png', '-landscape.png');

// Image-first sharing: Web Share API with files when available, download otherwise.
export function useBoxScoreShare({ captureRef, landscapeRef, meta }) {
  const [generating, setGenerating] = useState(false);

  const withGenerating = async (fn) => {
    if (generating) return undefined;
    setGenerating(true);
    try {
      return await fn();
    } finally {
      setGenerating(false);
    }
  };

  const shareImage = (variant, fileName, successMsg) => withGenerating(async () => {
    try {
      const node = variant === 'landscape' ? landscapeRef.current : captureRef.current;
      const dataUrl = await captureBoxScore(node, variant);
      if (!dataUrl) return;
      const file = new File([await dataUrlToBlob(dataUrl)], fileName, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        try {
          await navigator.share({ files: [file], title: meta.title, text: meta.title });
          return;
        } catch (err) { /* cancelled — fall through to download */ }
      }
      downloadDataUrl(dataUrl, fileName);
      toast.success(successMsg);
    } catch (err) {
      toast.error('Couldn\u2019t generate image');
    }
  });

  const handleShare = () => shareImage('portrait', meta.fileName, 'Box score image ready');
  const handleShareLandscape = () => shareImage('landscape', landscapeName(meta.fileName), 'Landscape box score ready');

  const handleDownload = () => withGenerating(async () => {
    try {
      const dataUrl = await captureBoxScore(landscapeRef.current, 'landscape');
      if (!dataUrl) return;
      downloadDataUrl(dataUrl, landscapeName(meta.fileName));
      toast.success('Box score saved');
    } catch (err) {
      toast.error('Couldn\u2019t generate image');
    }
  });

  // Deep links can't carry files, so the image goes to the clipboard for pasting.
  const copyImageToClipboard = async () => (await withGenerating(async () => {
    try {
      if (!navigator.clipboard || !window.ClipboardItem) return false;
      const dataUrl = await captureBoxScore(landscapeRef.current, 'landscape');
      if (!dataUrl) return false;
      const blob = await dataUrlToBlob(dataUrl);
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      return true;
    } catch (err) {
      console.error('image clipboard failed', err);
      return false;
    }
  })) || false;

  const handleWhatsApp = async () => {
    if (await copyImageToClipboard()) toast.success('Image copied — paste into WhatsApp chat');
    window.open(meta.whatsappHref, '_blank', 'noopener,noreferrer');
  };

  const handleMail = async () => {
    if (await copyImageToClipboard()) toast.success('Image copied — paste into your email body');
    window.location.href = meta.mailHref;
  };

  return { generating, handleShare, handleShareLandscape, handleDownload, handleWhatsApp, handleMail };
}
